/**
 * Rate Limiter Utility
 * 
 * Implements rate limiting for OTP sending to prevent email flooding.
 * Limits: 5 OTP requests per email/phone per time interval (default: 15 minutes)
 */

const RATE_LIMIT_KEY_PREFIX = 'otp_rate_limit_';
const MAX_REQUESTS = 5;
const TIME_WINDOW_MS = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Get the storage key for a given email/phone
 * @param {string} identifier - Email or phone number
 * @returns {string} Storage key
 */
const getStorageKey = (identifier) => {
  return `${RATE_LIMIT_KEY_PREFIX}${identifier.toLowerCase().trim()}`;
};

/**
 * Clean up expired entries from rate limit data
 * @param {Array} requests - Array of request timestamps
 * @param {number} currentTime - Current timestamp
 * @returns {Array} Filtered array with only valid timestamps
 */
const cleanExpiredEntries = (requests, currentTime) => {
  return requests.filter(timestamp => {
    const age = currentTime - timestamp;
    return age < TIME_WINDOW_MS;
  });
};

/**
 * Check if an OTP request is allowed for the given identifier
 * @param {string} identifier - Email or phone number
 * @returns {Object} { allowed: boolean, remaining: number, resetTime: number }
 * 
 * NOTE: Rate limiting has been disabled - always returns allowed: true
 */
export const checkRateLimit = (identifier) => {
  // Rate limiting disabled - always allow OTP requests
  return {
    allowed: true,
    remaining: 999,
    resetTime: null,
    message: null
  };
};

/**
 * Record an OTP request for the given identifier
 * @param {string} identifier - Email or phone number
 * @returns {boolean} True if request was recorded successfully
 */
export const recordOtpRequest = (identifier) => {
  if (!identifier) {
    return false;
  }

  const storageKey = getStorageKey(identifier);
  const currentTime = Date.now();
  
  try {
    // Get existing rate limit data
    const storedData = localStorage.getItem(storageKey);
    let requests = [];
    
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        requests = Array.isArray(parsed.requests) ? parsed.requests : [];
      } catch (e) {
        // Invalid data, reset
        requests = [];
      }
    }
    
    // Clean up expired entries
    requests = cleanExpiredEntries(requests, currentTime);
    
    // Add current request timestamp
    requests.push(currentTime);
    
    // Store updated data
    localStorage.setItem(storageKey, JSON.stringify({
      requests: requests,
      lastUpdated: currentTime
    }));
    
    return true;
  } catch (error) {
    console.error('Rate limit record error:', error);
    return false;
  }
};

/**
 * Clear rate limit data for a specific identifier
 * @param {string} identifier - Email or phone number
 */
export const clearRateLimit = (identifier) => {
  if (!identifier) {
    return;
  }

  const storageKey = getStorageKey(identifier);
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('Rate limit clear error:', error);
  }
};

/**
 * Clear all rate limit data (useful for testing or cleanup)
 */
export const clearAllRateLimits = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(RATE_LIMIT_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Clear all rate limits error:', error);
  }
};

/**
 * Get rate limit status for an identifier (for debugging/admin purposes)
 * @param {string} identifier - Email or phone number
 * @returns {Object} Rate limit status information
 */
export const getRateLimitStatus = (identifier) => {
  if (!identifier) {
    return null;
  }

  const storageKey = getStorageKey(identifier);
  const currentTime = Date.now();
  
  try {
    const storedData = localStorage.getItem(storageKey);
    if (!storedData) {
      return {
        identifier: identifier,
        requests: 0,
        remaining: MAX_REQUESTS,
        resetTime: null
      };
    }
    
    const parsed = JSON.parse(storedData);
    let requests = Array.isArray(parsed.requests) ? parsed.requests : [];
    
    // Clean up expired entries
    requests = cleanExpiredEntries(requests, currentTime);
    
    let resetTime = null;
    if (requests.length > 0) {
      const oldestRequest = Math.min(...requests);
      resetTime = oldestRequest + TIME_WINDOW_MS;
    }
    
    return {
      identifier: identifier,
      requests: requests.length,
      remaining: MAX_REQUESTS - requests.length,
      resetTime: resetTime,
      resetTimeFormatted: resetTime ? new Date(resetTime).toLocaleString() : null
    };
  } catch (error) {
    console.error('Get rate limit status error:', error);
    return null;
  }
};

